import { SelectField, Option } from '@/components/Form/SelectField';
import { InputField } from '@/components/Form/InputField';
import { SupportedLanguage } from '@/lib/i18n/config';
import { getCurrentLanguage } from '@/lib/i18n/getCurrentLanguage';
import {
  getLanguageLabel,
  getLanguageOptions,
} from '@/lib/i18n/languageLabels';
import { FieldErrors } from '@/lib/graphql/errors';
import { IconInfoCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

type ProfileBasicsFieldsProps = {
  firstName: string;
  lastName: string;
  language: SupportedLanguage;
  fieldErrors: FieldErrors;
  disabled?: boolean;
  appearance?: 'default' | 'design-system';
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onLanguageChange: (value: SupportedLanguage) => void;
};

export const ProfileBasicsFields = ({
  firstName,
  lastName,
  language,
  fieldErrors,
  disabled = false,
  appearance = 'default',
  onFirstNameChange,
  onLastNameChange,
  onLanguageChange,
}: ProfileBasicsFieldsProps) => {
  const { t } = useTranslation();
  const isWesternStyle = !['ja', 'ko', 'zh'].includes(getCurrentLanguage());
  const languageOptions = getLanguageOptions();
  const selectedLanguage =
    languageOptions.find((option) => option.value === language) ??
    languageOptions[0];

  const nameFields = [
    <InputField
      key="firstName"
      label={t('名') + ' *'}
      appearance={appearance}
      autoComplete="given-name"
      placeholder={t('氏名（名）を入力...')}
      className="!shadow-none"
      value={firstName}
      onChange={(event) => onFirstNameChange(event.currentTarget.value)}
      error={Boolean(fieldErrors.firstName?.length)}
      errors={fieldErrors.firstName}
      disabled={disabled}
      required
    />,
    <InputField
      key="lastName"
      label={t('姓') + ' *'}
      appearance={appearance}
      autoComplete="family-name"
      placeholder={t('氏名（姓）を入力...')}
      className="!shadow-none"
      value={lastName}
      onChange={(event) => onLastNameChange(event.currentTarget.value)}
      error={Boolean(fieldErrors.lastName?.length)}
      errors={fieldErrors.lastName}
      disabled={disabled}
      required
    />,
  ];

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        {isWesternStyle ? nameFields : [...nameFields].reverse()}
      </div>

      <div className="space-y-1">
        <SelectField<Option<SupportedLanguage>, false>
          label={t('言語') + ' *'}
          appearance={appearance}
          options={languageOptions}
          value={selectedLanguage}
          onChange={(option) => {
            if (!option) return;
            onLanguageChange(option.value);
          }}
          formatOptionLabel={(option) => getLanguageLabel(option.value)}
          isDisabled={disabled}
        />
        {fieldErrors.language && (
          <div className="space-y-1 text-sm text-red-600">
            {fieldErrors.language.map((message) => (
              <div key={message} className="flex items-start">
                <IconInfoCircle className="mr-2 mt-0.5 size-4 shrink-0" />
                <span>{message}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};
